/**
 * AnalystReviewPanel - AI KYC Analyst first-level review
 *
 * Runs the agent on demand: the analyst reads the applicant's ID verification and
 * sanctions/PEP/adverse-media screening results, then recommends a risk score.
 * Recommend-only: a human makes the final decision.
 */
import React, { useState } from 'react';
import {
  Bot,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ShieldCheck,
  ShieldAlert,
  Shield,
  Search,
} from 'lucide-react';
import { Button, Badge, Card } from './ui';
import {
  useAnalystReview,
  useInvestigateAdverseMedia,
  useReviewRecipes,
} from '../hooks/useAI';

const ADV_CONCLUSION = {
  true_match: { variant: 'high-risk', label: 'True match' },
  false_positive: { variant: 'low-risk', label: 'False positive' },
  needs_review: { variant: 'medium-risk', label: 'Needs review' },
};

const RISK_BADGE = {
  low: { variant: 'low-risk', label: 'Low Risk', Icon: ShieldCheck },
  medium: { variant: 'medium-risk', label: 'Medium Risk', Icon: Shield },
  high: { variant: 'high-risk', label: 'High Risk', Icon: ShieldAlert },
};

const REC_BADGE = {
  approve: { variant: 'success', label: 'Recommend Approval' },
  escalate: { variant: 'warning', label: 'Escalate to Human' },
};

export default function AnalystReviewPanel({ applicantId }) {
  const [review, setReview] = useState(null);
  const [adverse, setAdverse] = useState(null);
  const [recipe, setRecipe] = useState('');
  const mutation = useAnalystReview();
  const adverseMutation = useInvestigateAdverseMedia();
  const { data: recipeData } = useReviewRecipes();
  const recipes = recipeData?.recipes || [];

  const handleRun = async () => {
    try {
      const result = await mutation.mutateAsync(
        recipe ? { applicantId, recipe } : applicantId
      );
      setReview(result);
    } catch {
      // Error surfaced via mutation.isError below
    }
  };

  const handleInvestigate = async () => {
    try {
      const result = await adverseMutation.mutateAsync(applicantId);
      setAdverse(result);
    } catch {
      // Error surfaced via adverseMutation.isError below
    }
  };

  const risk = review ? RISK_BADGE[review.risk_level] || RISK_BADGE.medium : null;
  const rec = review ? REC_BADGE[review.recommendation] || REC_BADGE.escalate : null;

  return (
    <Card className="mb-5 border-teal-500/40 bg-gradient-to-br from-white to-teal-500/5 dark:from-grey-900 dark:to-teal-500/10">
      <div className="px-4 py-3 border-b border-grey-200 dark:border-grey-800 flex justify-between items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Bot size={16} className="text-teal-500" />
          <span className="text-sm font-semibold text-grey-900 dark:text-grey-50">
            AI Analyst Review
          </span>
          <Badge variant="info" className="gap-1">
            <Sparkles size={11} /> First-level
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          {recipes.length > 0 && (
            <select
              value={recipe}
              onChange={(e) => setRecipe(e.target.value)}
              disabled={mutation.isPending}
              title="Which review recipe to run"
              className="text-xs rounded-md border border-grey-200 dark:border-grey-700 bg-white dark:bg-grey-900 text-grey-700 dark:text-grey-200 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
            >
              <option value="">
                {recipes.find((r) => r.is_default)?.name
                  ? `Default (${recipes.find((r) => r.is_default).name})`
                  : 'Default (Full checklist)'}
              </option>
              {recipes.map((r) => (
                <option key={r.id} value={r.name}>
                  {r.name}
                </option>
              ))}
            </select>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleInvestigate}
            disabled={adverseMutation.isPending}
            title="Assess whether adverse-media hits refer to this person"
          >
            {adverseMutation.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Investigating…
              </>
            ) : (
              <>
                <Search size={14} /> Investigate adverse media
              </>
            )}
          </Button>
          <Button variant="secondary" size="sm" onClick={handleRun} disabled={mutation.isPending}>
            {mutation.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Reviewing…
              </>
            ) : review ? (
              'Re-run Review'
            ) : (
              'Run First-Level Review'
            )}
          </Button>
        </div>
      </div>

      <div className="p-4">
        {!review && !mutation.isPending && !mutation.isError && (
          <div className="text-grey-500 dark:text-grey-400 text-sm">
            The AI analyst reviews the ID verification and screening results, then
            recommends a risk score. Recommend-only: a human makes the final decision.
          </div>
        )}

        {mutation.isPending && (
          <div className="flex items-center gap-2 text-grey-500 dark:text-grey-400 text-sm">
            <Loader2 size={16} className="animate-spin" />
            Reviewing identity and screening results…
          </div>
        )}

        {mutation.isError && !mutation.isPending && (
          <div className="flex items-center gap-2 text-red-500 text-sm">
            <AlertTriangle size={16} />
            {mutation.error?.message || 'Review failed. Check AI configuration.'}
          </div>
        )}

        {review && !mutation.isPending && (
          <div className="space-y-4">
            {/* Checklist header (mirrors the compliance checklist) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="text-grey-400">Reviewer</div>
                <div className="font-medium text-grey-900 dark:text-grey-50">Megan (AI Agent)</div>
              </div>
              <div>
                <div className="text-grey-400">Date of Review</div>
                <div className="font-medium text-grey-900 dark:text-grey-50">
                  {review.generated_at ? new Date(review.generated_at).toLocaleDateString() : '-'}
                </div>
              </div>
              <div>
                <div className="text-grey-400">Final Risk Score</div>
                <Badge variant={risk.variant} className="gap-1 mt-0.5">
                  <risk.Icon size={12} /> {risk.label}
                </Badge>
              </div>
              <div>
                <div className="text-grey-400">Recommendation</div>
                <Badge variant={rec.variant} className="mt-0.5">{rec.label}</Badge>
              </div>
            </div>

            {review.recipe_name && (
              <div className="flex items-center gap-1.5 text-xs text-grey-500 dark:text-grey-400">
                <Sparkles size={12} className="text-teal-500" />
                Review recipe: <span className="font-medium text-grey-700 dark:text-grey-200">{review.recipe_name}</span>
              </div>
            )}

            {/* Rationale */}
            <div className="text-sm text-grey-700 dark:text-grey-200 bg-grey-50 dark:bg-grey-800/40 rounded-lg p-3 border border-grey-200 dark:border-grey-800">
              {review.rationale}
            </div>

            {/* Checklist table - regulator-ready evidence of process */}
            <div className="overflow-x-auto border border-grey-200 dark:border-grey-800 rounded-lg">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-grey-50 dark:bg-grey-800/40 text-left text-[11px] uppercase tracking-wide text-grey-500">
                    <th className="px-3 py-2 font-semibold">Review Item</th>
                    <th className="px-3 py-2 font-semibold w-[170px]">Result</th>
                    <th className="px-3 py-2 font-semibold">Notes / Comments</th>
                  </tr>
                </thead>
                <tbody>
                  {(review.checks || []).map((c, i) => (
                    <tr key={i} className="border-t border-grey-200 dark:border-grey-800 align-top">
                      <td className="px-3 py-2 font-medium text-grey-900 dark:text-grey-50">{c.name}</td>
                      <td className="px-3 py-2">
                        {c.status === 'pass' ? (
                          <span className="inline-flex items-center gap-1.5 text-green-600 dark:text-green-400">
                            <CheckCircle2 size={15} /> Addressed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-orange-600 dark:text-orange-400">
                            <AlertTriangle size={15} /> Escalate / Follow-up
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-grey-600 dark:text-grey-300">{c.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-xs text-grey-400">
              Completed by the AI agent and recorded in the tamper-evident audit log.
              Escalations are assigned to a human reviewer for approval.
            </div>

            <div className="text-[11px] text-grey-400 pt-1">
              Generated by {review.model_version}
              {review.generated_at ? ` · ${new Date(review.generated_at).toLocaleString()}` : ''}
            </div>
          </div>
        )}

        {adverseMutation.isError && !adverseMutation.isPending && (
          <div className="flex items-center gap-2 text-red-500 text-sm mt-3">
            <AlertTriangle size={16} />
            {adverseMutation.error?.message || 'Adverse-media investigation failed.'}
          </div>
        )}

        {adverse && !adverseMutation.isPending && (
          <div className="mt-4 pt-4 border-t border-grey-200 dark:border-grey-800 space-y-2">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-grey-900 dark:text-grey-50">
              <Search size={14} className="text-teal-500" /> Adverse media investigation
            </div>
            {adverse.summary && (
              <div className="text-sm text-grey-600 dark:text-grey-300">{adverse.summary}</div>
            )}
            {(adverse.assessments || []).map((a, i) => {
              const c = ADV_CONCLUSION[a.conclusion] || ADV_CONCLUSION.needs_review;
              return (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <Badge variant={c.variant} className="shrink-0">{c.label}</Badge>
                  <div>
                    <span className="font-medium text-grey-900 dark:text-grey-50">{a.matched_name}</span>
                    <span className="text-grey-500 dark:text-grey-400">: {a.rationale}</span>
                  </div>
                </div>
              );
            })}
            {(adverse.assessments || []).length === 0 && (
              <div className="text-sm text-grey-500">No adverse-media hits to investigate.</div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
